/**
 * tRPC Provider Component
 * Wraps the application with tRPC and React Query providers
 */
import type { Mutation, Query } from '@tanstack/react-query';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { notification } from 'antd';
import React, { useRef, useState } from 'react';
import { createTrpcClient, trpc } from './client';
import { buildTrpcErrorReportContext, renderReportIssueBtn } from '@/utils/apiErrorReport';
import { useNetworkStatus } from '@/context/NetworkStatusContext';
import { isNetworkError } from './networkError';

type TrpcProviderProps = {
  children: React.ReactNode;
};

/**
 * Extract the tRPC procedure path from a React Query key.
 * tRPC query keys are shaped as `[['router','procedure'], { input, type }]`.
 */
function extractProcedurePath(queryKey?: readonly unknown[]): string | undefined {
  if (!Array.isArray(queryKey) || queryKey.length === 0) return undefined;
  const first = queryKey[0];
  if (Array.isArray(first) && first.every((s) => typeof s === 'string')) {
    return first.join('.');
  }
  return undefined;
}

/**
 * Extract tRPC error context from an unknown error.
 * Network-level errors get a user-friendly message instead of the raw browser TypeError.
 * `procedurePathOverride` is extracted from the React Query key so the report
 * shows the actual procedure even for network errors (where the error object
 * carries no path).
 */
function extractTrpcErrorContext(error: unknown, procedurePathOverride?: string) {
  const errData = (error as { data?: Record<string, unknown> })?.data;
  const errShape = (error as { shape?: Record<string, unknown> })?.shape;
  const errMeta = (error as { meta?: { response?: unknown; responseJSON?: unknown } })?.meta;

  const message = isNetworkError(error)
    ? 'Network connection issue — please check your connection and try again'
    : (error instanceof Error ? error.message : 'An unexpected error occurred');

  return buildTrpcErrorReportContext({
    message,
    errorCode: errData?.code as string | undefined,
    procedurePath: procedurePathOverride ?? (errData?.path as string | undefined),
    responseData: errShape ?? errMeta?.responseJSON ?? errData,
  });
}

/**
 * Retry strategy: network errors get more attempts since they are transient;
 * auth errors are not retried at all.
 * React Query applies exponential backoff automatically between retries.
 */
export function shouldRetry(failureCount: number, error: unknown): boolean {
  const httpStatus = (error as { data?: { httpStatus?: number } })?.data?.httpStatus;
  if (httpStatus === 401 || httpStatus === 403) return false;
  if (isNetworkError(error)) return failureCount < 3;
  return failureCount < 1;
}

interface NetworkCallbacks {
  reportError: () => void;
  /** Returns true when the connection-unstable banner is already visible */
  isUnstable: () => boolean;
}

/**
 * Create global error handler for tRPC/React Query mutations.
 * Network-error toasts are suppressed when the connection banner is showing.
 */
function createMutationErrorHandler(network: NetworkCallbacks) {
  return (error: unknown, _variables: unknown, _context: unknown, mutation: Mutation<unknown, unknown, unknown, unknown>): void => {
    if (isNetworkError(error)) {
      network.reportError();
      if (network.isUnstable()) return;
    }
    if (mutation.meta?.suppressGlobalError) return;

    const procedurePath = extractProcedurePath(mutation.options.mutationKey as readonly unknown[] | undefined);

    // Stable key per error category → replaces instead of stacking
    const isNetwork = isNetworkError(error);
    const key = isNetwork ? 'trpc-network-error' : 'trpc-mutation-error';
    const ctx = extractTrpcErrorContext(error, procedurePath);
    notification.error({
      key,
      message: 'Operation Failed',
      description: ctx.errorMessage,
      duration: 10,
      btn: renderReportIssueBtn(ctx, key)
    });
  };
}

/**
 * Create global error handler for tRPC/React Query queries.
 *
 * Network errors never show a toast — they are reported to NetworkStatusContext
 * which surfaces the global "Connection unstable" banner once persistent. Toast
 * spam from transient blips and parallel-query fan-out was annoying operators.
 *
 * Non-network errors are also suppressed when:
 * - query.meta.suppressGlobalError is true (opt-in per query)
 */
function createQueryErrorHandler(network: NetworkCallbacks) {
  return (error: unknown, query: Query): void => {
    if (isNetworkError(error)) {
      network.reportError();
      return;
    }
    if (query.meta?.suppressGlobalError) return;

    const procedurePath = extractProcedurePath(query.queryKey as readonly unknown[]);

    // Stable key → replaces instead of stacking
    const key = 'trpc-query-error';
    const ctx = extractTrpcErrorContext(error, procedurePath);
    notification.error({
      key,
      message: 'Request Failed',
      description: ctx.errorMessage,
      duration: 10,
      btn: renderReportIssueBtn(ctx, key)
    });
  };
}

/**
 * TrpcProvider wraps the app with tRPC and React Query providers
 * This enables the use of tRPC hooks throughout the application
 */
export const TrpcProvider: React.FC<TrpcProviderProps> = ({ children }) => {
  const { reportNetworkError, isUnstable } = useNetworkStatus();

  // Use refs so the QueryClient callbacks (created once) always read the latest values
  const networkRef = useRef({ reportNetworkError, isUnstable });
  networkRef.current = { reportNetworkError, isUnstable };

  const networkCallbacks: NetworkCallbacks = {
    reportError: () => networkRef.current.reportNetworkError(),
    isUnstable: () => networkRef.current.isUnstable,
  };

  const [queryClient] = useState(
    () =>
      new QueryClient({
        queryCache: new QueryCache({
          onError: createQueryErrorHandler(networkCallbacks)
        }),
        mutationCache: new MutationCache({
          onError: createMutationErrorHandler(networkCallbacks)
        }),
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: false,
            retry: shouldRetry,
            staleTime: 30 * 1000
          },
        }
      })
  );

  const [trpcClient] = useState(() => createTrpcClient());

  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </trpc.Provider>
  );
};

export default TrpcProvider;
